import * as amplitude from "@amplitude/analytics-browser";
import { trace } from "@opentelemetry/api";

import { asyncEcho } from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";
import { recordEvent, recordEventAndFlush, TELEMETRY_CATEGORY_ATTRIBUTE } from "@/services/telemetry";

const log = createLogger("appsync");

interface EchoData {
  accountId: string;
  workspaceId: string;
  deviceRegistrationId: string;
  sentAt: number;
  status: "pending" | "success" | "failed";
  timeoutId: NodeJS.Timeout;
  echoIntervalMs: number;
  echoTimeoutMs: number;
}

// Map to store echo verification data
const echoMap = new Map<string, EchoData>();
let echoRegistered = false;
let echoIntervalId: NodeJS.Timeout | null = null;

function logEcho(echoData: EchoData) {
  const tracer = trace.getTracer("async-echo");

  // Find the echo string by searching the map
  let echoString = "";
  for (const [key, value] of echoMap.entries()) {
    if (value === echoData) {
      echoString = key;
      break;
    }
  }

  // Failed echoes are an AppSync-health reliability signal and ship unsampled; the once-a-minute
  // healthy echoes ride the sampled `api` rate. Attributes go in at creation so the sampler sees
  // the category, and the span starts at send time so its duration is the echo round-trip.
  const span = tracer.startSpan(`Echo results`, {
    startTime: new Date(echoData.sentAt),
    attributes: {
      [TELEMETRY_CATEGORY_ATTRIBUTE]: echoData.status === "failed" ? "reliability" : "api",
      echo: echoString,
      echoAccountId: echoData.accountId,
      echoWorkspaceId: echoData.workspaceId,
      echoDeviceRegistrationId: echoData.deviceRegistrationId,
      echoStatus: echoData.status,
      echoIntervalMs: echoData.echoIntervalMs,
      echoTimeoutMs: echoData.echoTimeoutMs,
      echoPendingCount: echoMap.size,
    },
  });
  span.end();
}

let limitConfirmPrompt = false;
let countDown = Array.from({ length: 5 }, (_, i) => i + 1);
const promptUserToRefresh = () => {
  if (limitConfirmPrompt) {
    return;
  }
  countDown.pop();
  recordEvent("appsync.echo.send_failed", { appsync: { consecutiveFailures: 5 - countDown.length } });
  if (countDown?.length <= 0) {
    amplitude.track("userRefreshWeb");
    void recordEventAndFlush("appsync.forced_reload", { appsync: { consecutiveFailures: 5 } }).finally(() =>
      window.location.reload(),
    );
    return;
  }
};

function createTimeoutHandler(echoString: string) {
  return () => {
    const echoData = echoMap.get(echoString);
    if (!echoData) return;

    if (echoData.status === "pending") {
      console.error("Echo verification failed - timeout after 30 seconds");
      echoData.status = "failed";
    }

    logEcho(echoData);
    echoMap.delete(echoString);
  };
}

// Function to start echo verification
function startEchoVerification(
  accountId: string,
  workspaceId: string,
  deviceRegistrationId: string,
  echoIntervalMs: number,
  echoTimeoutMs: number,
) {
  if (echoIntervalId) {
    clearInterval(echoIntervalId);
  }

  echoIntervalId = setInterval(() => {
    if (!echoRegistered) {
      console.error("No echo sender registered for echo verification");
      return;
    }

    sendNewEcho(accountId, workspaceId, deviceRegistrationId, echoIntervalMs, echoTimeoutMs)
      .then((data) => data)
      .catch(() => promptUserToRefresh());
  }, echoIntervalMs);
}

async function sendNewEcho(
  accountId: string,
  workspaceId: string,
  deviceRegistrationId: string,
  echoIntervalMs: number,
  echoTimeoutMs: number,
) {
  log.info(`Sending new echo for ${accountId} ${workspaceId} ${deviceRegistrationId}`);
  const now = new Date();
  const newEchoString = `accountId:${accountId}-workspaceId:${workspaceId}-deviceRegistrationId:${deviceRegistrationId}-${now.toISOString()}`;

  const echoData: EchoData = {
    accountId,
    workspaceId,
    deviceRegistrationId,
    sentAt: now.getTime(),
    status: "pending",
    timeoutId: setTimeout(createTimeoutHandler(newEchoString), echoTimeoutMs),
    echoIntervalMs,
    echoTimeoutMs,
  };

  echoMap.set(newEchoString, echoData);

  return asyncEcho(transport, { data: newEchoString })
    .then((response) => {
      // An echo that comes back empty never made it onto the AppSync round trip — treat as failed.
      if (!response?.data?.length) {
        return Promise.reject("Failed Echo");
      }
      return response;
    })
    .catch((error) => {
      console.error(`Error sending echo for ${newEchoString}`, error);
      return Promise.reject(error);
    });
}

export function registerAppSyncEcho(
  accountId: string,
  workspaceId: string,
  deviceRegistrationId: string,
  echoIntervalMs = 60000,
  echoTimeoutMs = 30000,
) {
  cleanup();
  echoRegistered = true;
  startEchoVerification(accountId, workspaceId, deviceRegistrationId, echoIntervalMs, echoTimeoutMs);
}

export function handleEchoResponse(echoString: string) {
  log.debug("Handling echo response", echoString);
  const echoData = echoMap.get(echoString);
  if (!echoData) return;

  clearTimeout(echoData.timeoutId);
  echoData.status = "success";
  logEcho(echoData);
  echoMap.delete(echoString);
}

export function cleanup() {
  if (echoIntervalId) {
    clearInterval(echoIntervalId);
  }

  // Clear all timeouts and remove all entries
  for (const [_, data] of echoMap) {
    clearTimeout(data.timeoutId);
  }
  echoMap.clear();

  echoIntervalId = null;
  echoRegistered = false;
}
